import React, { useRef, useState } from "react";
import {
    IconButton,
    Popover,
    TextField,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Typography,
    InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Search as SearchIcon } from "@material-ui/icons";
import { entitiesService } from "@services";
import useAsync from "@hooks/useAsync";
import { EntitySearchResult } from "@interfaces/EntitySearchResult";

const MIN_QUERY_LENGTH = 2;

const useStyles = makeStyles((theme) => ({
    paper: {
        width: 480,
        maxHeight: 480,
        padding: theme.spacing(2),
    },
    hint: {
        padding: theme.spacing(1, 0),
        color: theme.palette.text.secondary,
    },
    matches: {
        color: theme.palette.text.secondary,
    },
}));

/**
 * Поиск по всем справочникам разом (карточки/SKU) — по os_CardNo, sku,
 * Артикул модели, Наименование. Не про конкретный справочник — по всем
 * сразу, чтобы проверить, есть ли уже такая карточка/SKU в системе и в
 * каком она справочнике.
 */
const GlobalSearch = (): JSX.Element => {
    const classes = useStyles();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");

    const canLoad = query.trim().length >= MIN_QUERY_LENGTH;

    const { data, loadingStatus } = useAsync<EntitySearchResult[]>(entitiesService.searchEntities, {
        functionArgs: [query.trim()],
        canLoad,
        debounceInterval: 400,
    });

    const results = canLoad ? data || [] : [];

    return (
        <>
            <IconButton color="inherit" ref={anchorRef} onClick={() => setOpen(true)}>
                <SearchIcon />
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <div className={classes.paper}>
                    <TextField
                        autoFocus
                        fullWidth
                        placeholder="Название, SKU, os_CardNo, артикул модели..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            endAdornment: loadingStatus === "loading" && (
                                <InputAdornment position="end">
                                    <CircularProgress size={18} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {!canLoad && query.length > 0 && (
                        <Typography variant="body2" className={classes.hint}>
                            Минимум {MIN_QUERY_LENGTH} символа
                        </Typography>
                    )}

                    {canLoad && loadingStatus === "loaded" && results.length === 0 && (
                        <Typography variant="body2" className={classes.hint}>
                            Ничего не найдено
                        </Typography>
                    )}

                    {results.length > 0 && (
                        <List dense>
                            {results.map((r) => (
                                <ListItem key={`${r.et_id}-${r.e_id}`} divider>
                                    <ListItemText
                                        primary={`${r.et_name} — id ${r.e_id}`}
                                        secondary={r.matches
                                            .map((m) => `${m.a_name}: ${m.value}`)
                                            .join(", ")}
                                        secondaryTypographyProps={{ className: classes.matches }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </div>
            </Popover>
        </>
    );
};

export default GlobalSearch;
